import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { MarkdownRender } from '@gov-nx/component/web';
import { useExtraConfigContext } from '@gov-nx/core/app';
import { useLocale } from '@gov-nx/core/service';
import {
	DataBoxFolderType,
	isDataBoxTypePO,
	useDataBoxActionInfoContext,
	useDataBoxesContext,
} from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import { Button, EmptyMessage } from '@gov-nx/ui/web';
import { pageRouteResolver } from '@gov-nx/web';

const BaseInfoPageLayout = ({
	children,
	messageNotChosen = true,
}: {
	children?: ReactNode;
	messageNotChosen?: boolean;
}) => (
	<div className="px-6 py-4 h-full">
		<div className="flex flex-col h-full justify-center items-center">
			{messageNotChosen && (
				<div className="flex h-full justify-center items-center">
					<MessageNotChosen />
				</div>
			)}
			{children}
		</div>
	</div>
);

const NotificationsPage = () => {
	const { t } = useLocale(PageCode['datove-schranky']);
	const navigate = useNavigate();

	return (
		<>
			<div className="my-(--templates-margin-m) w-full h-[1px] bg-(--border-subtlest)" />
			<div className="flex h-full justify-center items-center">
				<EmptyMessage
					headline={null}
					image="notification">
					<p className="text-headline-xs! text-(--text-primary)!">
						{t('info-stranka.dulezita-zprava')}
					</p>
					<p>{t('info-stranka.zapnout-notifikace')}</p>
					<Button
						key="nastavit-notifikace-button"
						onClick={() => navigate(pageRouteResolver('nastaveni'))}
						color="primary"
						type="solid"
						size="s"
						aria-label={t('info-stranka.wcag.nastavit-notifikace')}>
						{t('info-stranka.nastavit-notifikace')}
					</Button>
				</EmptyMessage>
			</div>
		</>
	);
};

const AutoArchivePage = () => {
	const { t } = useLocale(PageCode['datove-schranky']);
	const navigate = useNavigate();

	return (
		<>
			<div className="my-(--templates-margin-m) w-full h-[1px] bg-(--border-subtlest)" />
			<div className="flex h-full justify-center items-center">
				<EmptyMessage
					headline={null}
					image="trezor">
					<p className="text-headline-xs! text-(--text-primary)!">
						{t('info-stranka.automaticke-mazani')}
					</p>
					<p>{t('info-stranka.automaticka-archivace')}</p>
					<MarkdownRender applyClasses={{ paragraph: 'text-secondary-700' }}>
						{t('automaticka-archivace.varovani.fikce')}
					</MarkdownRender>
					<Button
						key=""
						onClick={() => navigate(pageRouteResolver('nastaveni'))}
						color="primary"
						type="solid"
						size="s">
						{t('info-stranka.zapnout-archivaci')}
					</Button>
				</EmptyMessage>
			</div>
		</>
	);
};

const MessageNotChosen = () => {
	const { t } = useLocale(PageCode['datove-schranky']);

	return (
		<EmptyMessage
			headline={null}
			image="postcard">
			<p className="text-headline-xs! text-(--text-primary)!">
				{t('info-stranka.nevybrali-jste-zpravu')}
			</p>
			<p>{t('info-stranka.vyberte-zpravu')}</p>
		</EmptyMessage>
	);
};

export const ListStateContent = () => {
	const { automaticArchiveEnabled, notificationsEnabled } =
		useDataBoxesContext();
	const { folderType, emptyFolder, dataBox } = useDataBoxActionInfoContext();
	const { extraConfig } = useExtraConfigContext();
	const { displayAutoArchiveToggle, displayNotificationPage } =
		extraConfig.pageRoutes.databoxConfig;

	const shouldDisplayAutoArchivePage =
		!isDataBoxTypePO(dataBox) &&
		!automaticArchiveEnabled &&
		displayAutoArchiveToggle;

	const shouldDisplayNotificationsPage =
		!notificationsEnabled && displayNotificationPage;

	if (emptyFolder) {
		return (
			<BaseInfoPageLayout messageNotChosen={false}>
				<EmptyMessage
					headline=""
					image="postcard-placeholder"
					imageWidth={160}
					imageHeight={100}
				/>
			</BaseInfoPageLayout>
		);
	}

	switch (folderType) {
		case DataBoxFolderType.Received:
		case DataBoxFolderType.Sent:
			return (
				<BaseInfoPageLayout>
					{shouldDisplayAutoArchivePage ? (
						<AutoArchivePage />
					) : (
						shouldDisplayNotificationsPage && <NotificationsPage />
					)}
				</BaseInfoPageLayout>
			);
		case DataBoxFolderType.Archived:
			return (
				<BaseInfoPageLayout>
					{shouldDisplayAutoArchivePage && <AutoArchivePage />}
				</BaseInfoPageLayout>
			);
		case DataBoxFolderType.Trash:
			return <BaseInfoPageLayout />;
		default:
			return null;
	}
};
